<template>
  <div>
    <ul class="list-reset row-tight wrap-suppliers">
      <li
        v-for="supplier in resortChunks"
        :key="supplier.id"
        class="w-1/4 md:w-1/3 sm:w-full col-tight mb-8 sm:mb-5"
      >
        <div class="card">
          <a
            target="_blank"
            :href="supplier.__platform_url"
            :title="supplier.__platform_name"
          >
            <div class="aspect-ratio-3/2 mb-3">
              <carousel
                ref="carousel"
                v-if="
                  supplier.__platform_gallery &&
                    supplier.__platform_gallery.length > 1
                "
                class="relative h-full"
                :number-of-slides="supplier.__platform_gallery.length"
                :per-page-custom="[
                  [320, 1],
                  [848, 1],
                ]"
              >
                <template v-slot:default="{ carousel }">
                  <v-slide
                    v-for="(item, imageIndex) in supplier.__platform_gallery"
                    :key="imageIndex"
                  >
                    <div
                      class="cover-image rounded w-full h-full relative"
                      v-if="
                        imageIndex >= carousel.currentPage - 1 &&
                          imageIndex <= carousel.currentPage + 1
                      "
                    >
                      <div class="virtiual-div"></div>
                      <img
                        :src="getCloudinaryImage(item)"
                        class="w-full h-full cover-image rounded"
                      />
                    </div>
                  </v-slide>
                </template>

                <template v-slot:navigation="{ carousel }">
                  <div>
                    <div class="resort-room-arrow-nav">
                      <button
                        type="button"
                        class="resort-room-nav-btn"
                        @click.prevent.stop="carousel.goToPrevPage"
                        :disabled="!carousel.canAdvanceBackward"
                      >
                        <svg width="24" height="24" class="mx-auto">
                          <use
                            xlink:href="/img/sprite.svg#ic_chevron-left"
                          ></use>
                        </svg>
                      </button>
                      <div class="flex-1 h-0"></div>
                      <button
                        type="button"
                        class="resort-room-nav-btn"
                        @click.prevent.stop="carousel.goToNextPage"
                        :disabled="!carousel.canAdvanceForward"
                      >
                        <svg width="24" height="24" class="mx-auto">
                          <use
                            xlink:href="/img/sprite.svg#ic_chevron-right"
                          ></use>
                        </svg>
                      </button>
                    </div>

                    <div class="resort-room-dotted-nav rounded">
                      <div
                        v-for="(image,
                        imageIndex) in supplier.__platform_gallery"
                        :key="imageIndex"
                        class="carousel-bullet"
                        :class="{
                          'carousel-bullet--active':
                            carousel.currentPage === imageIndex,
                        }"
                        @click="carousel.goToPage(imageIndex)"
                      ></div>
                    </div>
                  </div>
                </template>

                <template v-slot:dots="{ carousel }">
                  <div
                    class="flex items-center"
                    v-if="supplier.__platform_gallery.length > 5"
                  >
                    <span
                      v-for="n in 5"
                      :key="n"
                      class="dot"
                      :class="{
                        'active-dot': activeDot(
                          n - 1,
                          carousel.currentPage,
                          supplier.__platform_gallery.length
                        ),
                      }"
                    ></span>
                  </div>
                  <div class="flex items-center" v-else>
                    <span
                      v-for="n in supplier.__platform_gallery.length"
                      :key="n"
                      class="dot"
                      :class="{
                        'active-dot': n - 1 === carousel.currentPage,
                      }"
                    ></span>
                  </div>
                </template>
              </carousel>
              <img
                class="rounded h-full"
                v-else
                :src="getCloudinaryImage(supplier.__platform_image)"
                :alt="supplier.__platform_name"
              />
            </div>

            <div class="card-info-container">
              <p class="mb-0 text-black-light-5 text-sm">
                {{ supplier.__platform_country_title
                }}{{
                  `${
                    filters.searchType === "resort"
                      ? ", " + supplier.__platform_destination_title
                      : ""
                  }`
                }}
              </p>
              <h4 class="fs-20 font-medium mb-0 mt-0 text-black-100">
                {{ supplier.__platform_name }}
              </h4>
              <p class="mb-0 text-black-100">
                <span>from </span>
                <currency-context>
                  <template v-slot="{ convert }">
                    <span
                      v-text="convert(supplier.__odyssey_price || supplier.__odyssey_price_per_day)"
                    ></span>
                  </template>
                </currency-context>
                <span>
                {{
                  `${ filters.searchType === "resort" ? "per night" : "per day"}`
                }}
                </span>
              </p>
            </div>
          </a>
        </div>
      </li>
    </ul>

    <div class="w-fit-content sm:w-full m-auto my-4 flex flex-col">
      <button
        v-if="visibleShowMore"
        @click="$emit('show-more')"
        class="btn-show-more btn btn-outlined btn-secondary mb-2 w-full"
      >
        Show more
      </button>

      <a v-if="filters.country" :href="viewAllCollections()">
        <button class="btn-view-all btn btn-secondary w-fit-content sm:w-full">
          View all {{ filters.searchType }}s in the
          {{ filters.country.label }}
          {{ filters.tag.length ? "for " + getTagName(filters.tag) : "" }}
        </button>
      </a>
    </div>
  </div>
</template>

<script>
import { Slide } from "vue-carousel";
import Carousel from "../core/Carousel";
import CurrencyContext from "../core/CurrencyContext";
import { getCloudinaryUrl } from "../../helpers";
import chunk from "lodash/chunk";

export default {
  components: {
    "v-slide": Slide,
    Carousel,
    CurrencyContext,
  },
  props: {
    page: {
      type: Number,
      required: true,
    },
    results: {
      type: Array,
      default: [],
      required: true,
    },
    filters: {
      type: Object,
      required: true,
    },
    tags: {
      type: Array,
      required: true,
    },
  },
  computed: {
    perPage() {
      const isDesktop = window.matchMedia("only screen and (min-width: 1201px)")
        .matches;
      const isTablet = window.matchMedia(
        "(min-width: 601px) and (max-width: 1200px)"
      ).matches;
      const isMobile = window.matchMedia("(max-width: 600px)").matches;

      if (isDesktop) return 12;
      else if (isTablet) return 9;
      else if (isMobile) return 5;
      return 12;
    },
    resortChunks() {
      let data = [];
      const arr = chunk(this.results, this.perPage);

      for (let i = 0; i < this.page; i++) {
        if (arr[i] && arr[i].length) {
          data = data.concat(arr[i]);
        }
      }

      return data;
    },
    visibleShowMore() {
      return this.results.length > this.page * this.perPage;
    },
  },
  methods: {
    viewAllCollections() {
      const existingQueryString = new URLSearchParams(window.location.search);
      if (this.filters.tag) {
        existingQueryString.append("goodFor", this.filters.tag);
      }

      existingQueryString.delete("type");
      existingQueryString.delete("country");
      existingQueryString.delete("suitable_for");

      const qs = existingQueryString.toString()
        ? "?" + existingQueryString.toString()
        : "";
      return (
        `/s/${this.filters.searchType}s/${this.filters.country.value}` + qs
      );
    },
    getTagName(selectedTags) {
      let results = this.tags.filter((_t) => selectedTags.includes(_t.value));
      if (results.length)
        return results.map((_r) => _r.title.toLowerCase()).join(", ");
      return null;
    },
    formatPrice(supplier) {
      const price =
        supplier.__odyssey_price || supplier.__odyssey_price_per_day;

      return price.toFixed(0).replace(/(\d)(?=(\d{3})+(?!\d))/g, "$1,");
    },
    activeDot(indexDot, currentPage, totalImages) {
      if (
        ((indexDot === 0 || indexDot === 1) && indexDot === currentPage) ||
        (indexDot === 3 && totalImages - 2 === currentPage) ||
        (indexDot === 4 && totalImages - 1 === currentPage) ||
        (indexDot === 2 &&
          currentPage !== 0 &&
          currentPage !== 1 &&
          currentPage !== totalImages - 2 &&
          currentPage !== totalImages - 1)
      ) {
        return true;
      }
      return false;
    },
    getCloudinaryImage(url) {
      return getCloudinaryUrl(url, { width: 600 });
    },
  },
};
</script>

<style scoped>
.wrap-suppliers ::v-deep .VueCarousel,
.wrap-suppliers ::v-deep .VueCarousel-inner {
  height: 100% !important;
}

.wrap-suppliers ::v-deep .VueCarousel-wrapper {
  overflow-x: hidden !important;
}

.VueCarousel-slide {
  max-width: unset !important;
}

.btn-show-more,
.btn-view-all {
  min-width: 300px;
}

.resort-room-nav-btn {
  transform: scale(0.75);
}

.dot {
  background: white;
  border-radius: 50%;
  height: 6px;
  margin-left: 2.5px;
  margin-right: 2.5px;
  min-width: 6px;
  opacity: 0.6;
  transition: 0.3s;
  width: 6px;
}

.active-dot {
  background: white !important;
  opacity: 1;
  height: 8px;
  width: 8px;
}

.virtiual-div {
  position: absolute;
  width: 100%;
  height: 100%;
  z-index: 9999;
  border-radius: 0.25rem;
  background: linear-gradient(180deg, transparent 80%, #00000040);
}
</style>
